import PageShell from '../components/PageShell';
import './TeethWhitening.css';

// Replicating the EXACT data from POC to ensure 100% match
const WHITENING_GLANCE = [
  {
    question: 'What is it?',
    answer:
      'A cosmetic procedure that lightens the natural colour of teeth using peroxide-based bleaching agents.',
  },
  {
    question: 'Who may benefit?',
    answer:
      'Adults with healthy teeth and gums who want to improve the appearance of naturally discoloured teeth.',
  },
  {
    question: 'What are the options?',
    answer:
      'Dentist-supervised home bleaching, in-office bleaching, a combination of the two, or over-the-counter products.',
  },
  {
    question: 'What are the most common side effects?',
    answer: 'Temporary tooth sensitivity and temporary gum irritation.',
  },
  {
    question: 'Will fillings and crowns whiten?',
    answer:
      'No. Bleaching changes natural tooth structure but does not change the colour of existing restorations.',
  },
  {
    question: 'How long does it last?',
    answer:
      'Results are not permanent. Diet, tobacco, oral hygiene and ageing all affect how long they hold.',
  },
  {
    question: 'Is a professional evaluation necessary?',
    answer:
      'A dental examination is recommended first, to find the cause of the discoloration and decide whether whitening is appropriate.',
  },
];

const WHITENING_SECTIONS = [
  {
    id: 'what-is-it',
    number: 1,
    title: 'What is dental bleaching?',
    blocks: [
      {
        type: 'paragraph',
        content:
          'Dental bleaching is a cosmetic procedure that lightens the natural colour of your teeth and reduces discoloration caused by stains inside or on the tooth.',
      },
      {type: 'subhead', content: 'The active ingredients'},
      {
        type: 'list',
        content: [
          'Hydrogen peroxide',
          'Carbamide peroxide, which breaks down and releases hydrogen peroxide',
        ],
      },
      {
        type: 'paragraph',
        content:
          'Peroxide molecules pass into enamel and dentin and break up the coloured molecules responsible for the discoloration.',
      },
      {
        type: 'paragraph',
        content:
          'This is different from simply removing surface stains. A professional cleaning or a whitening toothpaste can lift stains from the outside of the tooth. Peroxide bleaching changes the colour of the natural tooth underneath.',
      },
      {
        type: 'note',
        content:
          'Bleaching changes the colour of natural teeth only. It does not whiten crowns, veneers, fillings, bridges, implants, or any other tooth-coloured restoration.',
      },
    ],
    details: [
      {
        summary: 'What is actually in whitening products',
        blocks: [
          {type: 'subhead', content: 'Hydrogen peroxide'},
          {
            type: 'list',
            content: [
              'Acts directly, without breaking down first',
              'Common in professional and in-office systems',
              'Usually a higher concentration than home products',
            ],
          },
          {type: 'subhead', content: 'Carbamide peroxide'},
          {
            type: 'list',
            content: [
              'Breaks down into hydrogen peroxide and urea',
              'Common in home bleaching systems',
              'Releases peroxide more gradually',
            ],
          },
          {
            type: 'paragraph',
            content:
              'Products also contain supporting ingredients that vary by manufacturer: water, glycerin, thickening agents, desensitising ingredients such as potassium nitrate, fluoride, pH modifiers and flavouring.',
          },
        ],
      },
    ],
  },
  {
    id: 'discoloration',
    number: 2,
    title: 'Why teeth discolour',
    lede: 'Knowing the cause matters, because it decides which treatment will work.',
    blocks: [
      {type: 'subhead', content: 'Stains on the surface'},
      {type: 'paragraph', content: 'These sit on the outside of the tooth. Common causes:'},
      {
        type: 'list',
        content: [
          'Coffee',
          'Tea',
          'Red wine',
          'Tobacco',
          'Dark-coloured foods',
          'Certain mouthrinses',
          'Plaque and calculus',
          'Poor oral hygiene',
        ],
      },
      {
        type: 'paragraph',
        content:
          'Treatment may be as simple as a professional cleaning, polishing, stain removal or a whitening toothpaste, with bleaching where it is appropriate. Many surface stains improve substantially without any bleaching at all.',
      },
      {type: 'subhead', content: 'Discoloration inside the tooth'},
      {type: 'paragraph', content: 'This sits within the enamel or dentin. Possible causes:'},
      {
        type: 'list',
        content: [
          'Ageing',
          'Fluorosis',
          'Tetracycline or certain antibiotics taken while teeth were developing',
          'Enamel or dentin that did not form normally',
          'Injury to the tooth',
          'Bleeding inside the tooth',
          'A tooth whose nerve has died',
          'Certain medical conditions',
          'Natural dentin colour showing through thinner enamel',
        ],
      },
      {
        type: 'paragraph',
        content:
          'Discoloration inside the tooth responds differently depending on its cause and severity. Tetracycline staining, for example, can need prolonged treatment and may not disappear completely.',
      },
    ],
    details: [
      {
        summary: 'Teeth that have had root canal treatment',
        blocks: [
          {
            type: 'paragraph',
            content:
              'A tooth that has had root canal treatment can darken from the inside. Bleaching applied to the outside may not correct that kind of discoloration.',
          },
          {
            type: 'paragraph',
            content:
              'A dentist may consider internal bleaching instead, placing the bleaching agent inside the treated tooth. This needs proper assessment first, because rare complications affecting the root have been reported.',
          },
        ],
      },
    ],
  },
  {
    id: 'who-it-suits',
    number: 3,
    title: 'Who it suits',
    lede: 'Whitening may suit you if you want a lighter tooth colour and your mouth is in reasonable health.',
    blocks: [
      {
        type: 'checklist',
        content: [
          'Generally yellow or darker natural tooth colour',
          'Discoloration that has come with age',
          'Surface staining that is still there after a professional cleaning',
          'Mild to moderate discoloration inside the tooth',
          'Brown discoloration from fluorosis',
          'Tetracycline staining, in selected cases',
          'Lightening the shade before cosmetic dental work',
          'Smile enhancement',
        ],
      },
      {
        type: 'paragraph',
        content: 'Results are most predictable when the discoloration is in natural, living teeth.',
      },
    ],
    details: [
      {
        summary: 'What results to expect',
        blocks: [
          {
            type: 'paragraph',
            content:
              'Whitening can make teeth lighter, but no one can promise a specific shade. Worth knowing before you start:',
          },
          {
            type: 'list',
            content: [
              'Results vary from person to person',
              'Not every kind of discoloration responds equally',
              'Deep internal stains may need prolonged treatment',
              'Some discoloration is better treated with restorative work instead',
              'Existing fillings, crowns and veneers will not whiten',
              'Whitening does not prevent future staining',
              'Maintenance may be needed',
            ],
          },
          {
            type: 'paragraph',
            content:
              'Where discoloration is severe and inside the tooth, alternatives include composite bonding, porcelain veneers, crowns, microabrasion and internal bleaching. Which one suits depends on the diagnosis.',
          },
        ],
      },
    ],
  },
  {
    id: 'not-now',
    number: 4,
    title: 'When it is not the right time',
    lede: 'A dental examination should come first. Whitening is delayed or avoided when there is untreated disease, or anything that would make treatment unsafe or unpredictable.',
    blocks: [
      {type: 'subhead', content: 'Treated or checked first'},
      {
        type: 'list',
        content: [
          'Active tooth decay',
          'Untreated gum disease',
          'Significant gum inflammation',
          'Exposed root surfaces',
          'Cracked teeth',
          'Significant enamel defects',
          'Untreated problems with the nerve of the tooth',
          'Severe sensitivity',
          'Leaking or failing fillings',
          'Significant tooth wear',
          'Discoloration with no known cause',
          'Active sores or lesions in the mouth',
          'Poor oral hygiene',
        ],
      },
      {
        type: 'note',
        content:
          'The ADA recommends an oral and dental assessment before bleaching, to identify the cause of the discoloration, check sensitivity and allergies, and decide whether whitening is appropriate.',
      },
    ],
    details: [
      {
        summary: 'What the dentist checks before you start',
        blocks: [
          {type: 'subhead', content: 'Your medical history'},
          {
            type: 'list',
            content: [
              'General health and medications',
              'Allergies',
              'Pregnancy or breastfeeding',
              'Previous whitening',
              'Any history of tooth sensitivity',
            ],
          },
          {type: 'subhead', content: 'Your dental history'},
          {
            type: 'list',
            content: [
              'Previous fillings and crowns',
              'Injury to a tooth',
              'Root canal treatment',
              'Previous orthodontic treatment',
              'Fluorosis or tetracycline exposure',
            ],
          },
          {type: 'subhead', content: 'The examination'},
          {
            type: 'paragraph',
            content:
              'The dentist looks at decay, existing fillings, cracks, enamel defects, the condition of your gums, any recession, tooth wear, the nerve of the tooth where relevant, and the discoloration itself.',
          },
          {type: 'subhead', content: 'Recording your starting shade'},
          {
            type: 'paragraph',
            content:
              'A baseline shade is ideally recorded before treatment, using a shade guide, a digital shade device, or standardised photographs. Your final result depends on where you started and what caused the discoloration.',
          },
        ],
      },
    ],
  },
  {
    id: 'cautions',
    number: 5,
    title: 'Care and cautions',
    lede: 'Situations where whitening needs extra thought, or a conversation first.',
    blocks: [
      {
        type: 'pairs',
        pairs: [
          {
            title: 'Severe tooth sensitivity',
            body: 'If your teeth are already sensitive, whitening can make that worse for a while.',
          },
          {
            title: 'Large fillings or crowns',
            body: 'Bleaching does not change the colour of existing restorations, so a mismatch can appear afterwards.',
          },
          {
            title: 'Extensive enamel defects',
            body: 'Thin or defective enamel can respond unpredictably and may be more sensitive.',
          },
          {
            title: 'Receding gums',
            body: 'Exposed root surfaces do not bleach the way enamel does, and can be particularly sensitive.',
          },
          {
            title: 'Cracked teeth',
            body: 'Whitening can aggravate sensitivity, so it is usually postponed until the tooth has been assessed.',
          },
          {
            title: 'Active decay or gum disease',
            body: 'These are generally treated before any elective whitening.',
          },
          {
            title: 'Pregnancy or breastfeeding',
            body: 'Whitening is elective and safety data are limited, so many clinicians suggest waiting.',
          },
          {
            title: 'Children and adolescents',
            body: 'Routine whitening of all the teeth is generally not recommended for young patients without a dental reason for it.',
          },
          {
            title: 'Allergies',
            body: 'Tell your dentist about any known allergies or past reactions to dental materials.',
          },
        ],
      },
    ],
    details: [
      {
        summary: 'Sensitivity, and how to manage it',
        blocks: [
          {
            type: 'paragraph',
            content:
              'Tooth sensitivity is the most common side effect of whitening living teeth. It can feel like a sharp response to cold, a brief zing, generalised sensitivity, or discomfort when you breathe cold air. It is usually temporary, and the ADA reports it is generally mild and passes.',
          },
          {type: 'subhead', content: 'What helps'},
          {
            type: 'list',
            content: [
              'Whitening less often',
              'Wearing the trays for less time',
              'Pausing treatment',
              'Using a lower-concentration product',
              'Using a desensitising toothpaste',
              'Using fluoride products',
              'Products containing potassium nitrate',
              'Treating any underlying dental problem',
            ],
          },
          {
            type: 'note',
            content:
              'Do not simply put up with severe or persistent pain. Contact your dentist to have it looked at.',
          },
        ],
      },
      {
        summary: 'Gum irritation',
        blocks: [
          {
            type: 'paragraph',
            content:
              'Bleaching gel touching your gums can cause a burning feeling, whitening or blanching of the tissue, redness, irritation and temporary discomfort.',
          },
          {
            type: 'paragraph',
            content:
              'It usually comes from too much gel, poorly fitting trays, gel leaking, or inadequate protection during an in-office treatment. It is generally temporary, but significant irritation should be looked at.',
          },
        ],
      },
      {
        summary: 'Safety and side effects',
        blocks: [
          {type: 'subhead', content: 'Common'},
          {
            type: 'list',
            content: ['Temporary tooth sensitivity', 'Gum irritation', 'Mild discomfort in the mouth'],
          },
          {type: 'subhead', content: 'Less common'},
          {
            type: 'list',
            content: [
              'Chemical irritation of the soft tissue',
              'More sensitivity in people who were already sensitive',
              'Uneven whitening',
              'A colour mismatch with existing fillings or crowns',
            ],
          },
          {type: 'subhead', content: 'Contact your dentist if you have'},
          {
            type: 'list',
            content: [
              'Severe pain',
              'Sensitivity that does not settle',
              'Significant gum irritation',
              'Swelling',
              'Unexpected tooth discoloration',
              'Symptoms lasting longer than you were told to expect',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'options',
    number: 6,
    title: 'Your treatment options',
    lede: 'There are four main routes. A dentist helps you pick based on your diagnosis and how sensitive your teeth are.',
    blocks: [
      {
        type: 'pairs',
        pairs: [
          {
            title: 'Dentist-supervised home whitening',
            body: 'Custom-made trays and professional gel, used at home to a plan your dentist sets.',
          },
          {
            title: 'In-office whitening',
            body: 'A higher-concentration gel applied by the dental team, with your gums protected.',
          },
          {
            title: 'Combination',
            body: 'An in-office treatment first, then dentist-supervised whitening at home.',
          },
          {
            title: 'Over-the-counter products',
            body: 'Strips, gels, toothpastes, paint-on products and some rinses that you buy yourself.',
          },
        ],
      },
      {
        type: 'paragraph',
        content:
          'Professional treatment generally gives more control over the diagnosis, the concentration used, how it is applied, how your gums are protected, and how the result is monitored.',
      },
    ],
    details: [
      {
        summary: 'Home whitening with custom trays, step by step',
        blocks: [
          {
            type: 'paragraph',
            content:
              'For many people this is an effective and convenient option. Your dentist provides custom-made trays, the gel, a shade guide and a desensitising product if you need one.',
          },
          {
            type: 'list',
            content: [
              'Examination, to check whitening is appropriate for you',
              'Your starting shade is recorded',
              'A professional cleaning, where that is useful first',
              'Trays are made to fit your teeth closely, keeping gel off your gums',
              'You are given the gel and instructions for using it',
              'You apply only the amount of gel you were told to use, in each tooth compartment',
              'You wear the trays for the time your dentist prescribed',
              'A review, to check the shade change, sensitivity, your gums, and whether more treatment is worthwhile',
            ],
          },
          {
            type: 'note',
            content: 'More gel does not whiten faster. Excess gel mostly ends up irritating your gums.',
          },
          {
            type: 'paragraph',
            content:
              'Wear time depends on the product concentration, the manufacturer instructions, your sensitivity and your dentist plan. Published schedules vary widely, including daily treatment over several weeks.',
          },
        ],
      },
      {
        summary: 'In-office whitening, step by step',
        blocks: [
          {
            type: 'paragraph',
            content:
              'In-office whitening is carried out by the dental team and generally uses a stronger gel than home treatment.',
          },
          {
            type: 'list',
            content: [
              'Examination and diagnosis, to establish the cause of the discoloration',
              'Your starting shade is recorded, with photographs where useful',
              'Plaque and surface stains are cleaned off',
              'Your gums and soft tissue are protected with a barrier and careful isolation',
              'The bleaching gel is applied according to the product instructions',
              'Some systems use a light, many do not',
              'The team watches for sensitivity, gum irritation, discomfort and any leaking gel',
              'The gel is removed and reapplied as the product directs',
              'A final check of shade, sensitivity, your gums, and how you feel about the result',
            ],
          },
          {
            type: 'paragraph',
            content:
              'Careful isolation matters, because concentrated peroxide can irritate or burn soft tissue. On lights: current evidence suggests light, LED and laser activation does not reliably improve the result, and can increase sensitivity.',
          },
        ],
      },
      {
        summary: 'In-office or at home: how they compare',
        blocks: [
          {
            type: 'pairs',
            pairs: [
              {title: 'Where', body: 'In-office: at the practice. At home: wherever you are.'},
              {title: 'Strength', body: 'In-office: generally higher. At home: generally lower.'},
              {title: 'How long', body: 'In-office: shorter. At home: longer.'},
              {title: 'Custom trays', body: 'In-office: usually not needed. At home: yes.'},
              {
                title: 'Gum protection',
                body: 'In-office: applied by the team. At home: the tray design keeps gel off your gums.',
              },
              {
                title: 'Effort',
                body: 'In-office: fastest route. At home: convenient, but you have to keep to the plan.',
              },
              {title: 'Cost', body: 'In-office: usually higher. At home: usually lower.'},
              {
                title: 'Topping up',
                body: 'In-office: often needs a future visit. At home: easy to repeat with prescribed gel.',
              },
            ],
          },
          {
            type: 'paragraph',
            content:
              'Faster does not mean a better long-term result. Evidence supports both routes, and the choice comes down to your preferences, your sensitivity and your diagnosis.',
          },
        ],
      },
      {
        summary: 'Combination whitening',
        blocks: [
          {
            type: 'paragraph',
            content:
              'Some people have an in-office treatment first and then continue at home under their dentist supervision. That can give a rapid initial improvement, then let you fine-tune the final shade and make maintenance easier. The exact sequence is set for you.',
          },
        ],
      },
      {
        summary: 'Whitening toothpaste, strips and other products you can buy',
        blocks: [
          {
            type: 'paragraph',
            content:
              'Whitening toothpaste is generally not the same thing as professional bleaching. Most of them work by removing surface stains with mild abrasives, though some contain low levels of peroxide. Useful for keeping a brighter appearance, but it usually will not change the colour inside the tooth.',
          },
          {
            type: 'paragraph',
            content:
              'Over-the-counter options include hydrogen peroxide strips, carbamide peroxide products, gels, paint-on products, toothpastes and rinses. They can work, but concentration, treatment time, fit and quality vary a lot. Follow the manufacturer directions, and stop if you get significant irritation or pain.',
          },
        ],
      },
      {
        summary: 'If you are planning fillings, crowns or veneers',
        blocks: [
          {
            type: 'paragraph',
            content:
              'Bleaching changes natural enamel and dentin. It does not change composite fillings, porcelain veneers, crowns, bridges, implants, implant crowns or ceramic restorations.',
          },
          {
            type: 'paragraph',
            content:
              'So if you have visible restorations at the front of your mouth, whitening can leave them looking out of place. Where cosmetic work is planned, the usual order is: whiten first, let the shade settle, then choose the restoration shade and do the restorative work. That way the new work is not matched to a shade your teeth no longer are.',
          },
        ],
      },
    ],
  },
  {
    id: 'during-after',
    number: 7,
    title: 'During and after treatment',
    blocks: [
      {type: 'subhead', content: 'While you are whitening'},
      {
        type: 'list',
        content: [
          'Keep up your oral hygiene: brush twice a day, clean between your teeth daily, keep your check-ups',
          'Use only the amount of gel you were told to use',
          'Keep to the wear time your dentist set, and do not extend it',
          'You may choose to go easy on coffee, tea, red wine, tobacco and dark-coloured food and drink',
          'Use the desensitising products you were recommended, and say something if sensitivity builds',
        ],
      },
      {
        type: 'paragraph',
        content:
          'A strict "white diet" is not necessarily required. Good oral hygiene and sticking to the plan matter more.',
      },
      {type: 'subhead', content: 'Straight after treatment'},
      {
        type: 'paragraph',
        content:
          'Temporary sensitivity, mild gum irritation and a temporarily brighter appearance are all normal. Judge the final shade once your teeth have had time to settle, rather than from how they look immediately afterwards.',
      },
      {type: 'subhead', content: 'How long it lasts'},
      {
        type: 'paragraph',
        content:
          'Whitening is not permanent. How long your result holds depends on your diet, tobacco use, oral hygiene, age, your original tooth colour, the type of discoloration, the protocol used and your habits. A top-up may be worthwhile from time to time.',
      },
    ],
    details: [
      {
        summary: 'What affects how well it works',
        blocks: [
          {
            type: 'pairs',
            pairs: [
              {
                title: 'Your tooth colour',
                body: 'Yellowish teeth often respond more predictably than grey ones or deep internal staining.',
              },
              {
                title: 'The type of stain',
                body: 'Fluorosis, tetracycline staining, injury and nerve-related discoloration can each need a different approach.',
              },
              {title: 'Age', body: 'Older teeth tend to have darker dentin and thinner enamel.'},
              {title: 'Existing restorations', body: 'Fillings, crowns and veneers do not bleach.'},
              {
                title: 'Sticking to the plan',
                body: 'Home treatment depends on following the instructions you were given.',
              },
              {
                title: 'Strength and exposure time',
                body: 'Stronger and longer is not automatically better, and can increase sensitivity.',
              },
            ],
          },
        ],
      },
    ],
  },
];

const WHITENING_DISCLAIMER =
  'Educational information only. This is not a substitute for a professional dental examination, diagnosis or treatment. Whitening is not appropriate for everyone. Your options, materials, concentrations, treatment time, risks and likely results should be decided by a licensed dental professional after a proper assessment. Always follow the instructions given by your dentist and by the manufacturer of the product.';

// Helper UI functions
function Tick() {
  return (
    <svg className="tw-tick" width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="m4.5 12.5 4.5 4.5L19.5 6.5"
        stroke="#C43B71"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function renderBlock(block, idx) {
  switch (block.type) {
    case 'subhead':
      return (
        <h3 key={idx} className="tw-subhead">
          {block.content}
        </h3>
      );
    case 'paragraph':
      return (
        <p key={idx} className="tw-p">
          {block.content}
        </p>
      );
    case 'note':
      return (
        <p key={idx} className="tw-note">
          {block.content}
        </p>
      );
    case 'list':
    case 'checklist': {
      const isCheck = block.type === 'checklist';
      return (
        <ul key={idx} className="tw-list">
          {block.content.map((item, i) => (
            <li key={i}>
              {isCheck ? <Tick /> : <span className="tw-dot" />}
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    case 'pairs':
      return (
        <dl key={idx} className="tw-pairs">
          {block.pairs.map((pair, i) => (
            <div key={i}>
              <dt>{pair.title}</dt>
              <dd>{pair.body}</dd>
            </div>
          ))}
        </dl>
      );
    default:
      return null;
  }
}

export default function TeethWhitening() {
  return (
    <PageShell title="Teeth Whitening" showBackButton={true}>
      <div className="tw-container">
        <p className="tw-intro">
          A brighter smile can lift your confidence. Here is what whitening can and cannot do, who it suits,
          and what to expect.
        </p>

        <div className="tw-glance-card">
          <dl style={{margin: 0}}>
            {WHITENING_GLANCE.map((item, i) => (
              <div key={i} className="tw-glance-item">
                <dt>{item.question}</dt>
                <dd>{item.answer}</dd>
              </div>
            ))}
          </dl>
        </div>

        {WHITENING_SECTIONS.map((section) => (
          <section key={section.id} id={section.id} className="tw-section">
            <h2>
              <span>{section.number}. </span>
              {section.title}
            </h2>

            {section.lede && <p className="tw-lede">{section.lede}</p>}

            {section.blocks.map((b, i) => renderBlock(b, i))}

            {section.details &&
              section.details.map((detail, i) => (
                <details key={i} className="tw-details">
                  <summary>
                    <svg style={{marginRight: 8}} width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 5l7 7-7 7"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {detail.summary}
                  </summary>
                  <div className="tw-details-content">
                    {detail.blocks.map((b, idx) => renderBlock(b, idx))}
                  </div>
                </details>
              ))}
          </section>
        ))}

        <p className="tw-disclaimer">{WHITENING_DISCLAIMER}</p>
      </div>
    </PageShell>
  );
}
